import { format } from 'date-fns';
import {
  deleteImageFromIndexedDB,
  deleteImagesFromIndexedDB,
  getAllImageIds,
  getImageRecordFromIndexedDB,
  saveImageToIndexedDB,
} from '@/utils/imageStorage';
import { UPLOAD_CONFIG } from '@/utils/uploadConfig';
import { isImageSpaceTempLocalId } from '@/utils/imageSpaceImportId';

export { isImageSpaceTempLocalId };

/**
 * T-A14 / T-A19：图片空间上传热路径的 IndexedDB 备份策略。
 * - B（idbAsync=true，默认）：fire-and-forget，不阻塞 OSS flush
 * - A（idbAsync=false）：await 写入后再继续（保守/回滚）
 * - T-A19：定时 GC / 配额回收 / 刷新恢复 pending
 */

/** LIB-* 临时键最大保留时长（防异步写晚到与孤儿），默认 48h */
export const IMAGE_SPACE_IDB_TEMP_TTL_MS = 48 * 60 * 60 * 1000;

/** 后台定时 GC 间隔，默认 30min */
export const IMAGE_SPACE_IDB_GC_INTERVAL_MS = 30 * 60 * 1000;

export function isImageSpaceIdbAsyncEnabled(config = UPLOAD_CONFIG) {
  return config?.idbAsync !== false;
}

/**
 * @param {string} imageId
 * @param {Blob|File} blobOrFile
 * @param {{ async?: boolean }} [options]
 * @returns {Promise<boolean>} async 模式下立即 resolve true；同步模式返回写入结果
 */
export async function scheduleImageSpaceIdbBackup(imageId, blobOrFile, options = {}) {
  const asyncMode = options.async != null
    ? Boolean(options.async)
    : isImageSpaceIdbAsyncEnabled();
  if (!imageId || blobOrFile == null) return false;

  const writeOnce = () => Promise.resolve()
    .then(() => saveImageToIndexedDB(imageId, blobOrFile, { skipServerUpload: true }))
    .then(() => true);

  const run = () => writeOnce()
    .catch(async (err) => {
      const msg = String(err?.message || err || '');
      // T-A19：配额满时清一半最旧 LIB-* 再试一次；仍失败则静默
      if (msg.includes('QUOTA_EXCEEDED')) {
        try {
          await reclaimImageSpaceIdbQuota();
          return await writeOnce();
        } catch (retryErr) {
          console.warn(`[imageSpaceIdb] backup retry failed id=${imageId}:`, retryErr?.message || retryErr);
          return false;
        }
      }
      console.warn(`[imageSpaceIdb] backup failed id=${imageId}:`, msg);
      return false;
    });

  if (asyncMode) {
    void run();
    return true;
  }
  return run();
}

/**
 * 成功上传后清理 LIB-* 等临时键（fire-and-forget）。
 * @param {Iterable<string>|string[]} ids
 */
export function scheduleImageSpaceIdbGc(ids) {
  const list = [...new Set(
    (Array.isArray(ids) ? ids : ids ? [...ids] : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  )];
  if (list.length === 0) return;
  void Promise.allSettled(
    list.map((id) => Promise.resolve()
      .then(() => deleteImageFromIndexedDB(id))
      .catch(() => false)),
  );
}

/**
 * 从 enrich 结果收集可 GC 的临时本地 id（已有受管 url 的成功项）。
 * @param {object[]} enrichedNewImages
 * @returns {string[]}
 */
export function collectImageSpaceIdbTempIdsForGc(enrichedNewImages) {
  const out = [];
  for (const item of Array.isArray(enrichedNewImages) ? enrichedNewImages : []) {
    const url = String(item?.url || '').trim();
    if (!url || url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('indexeddb://')) {
      continue;
    }
    const localId = item?.clientLocalId != null ? String(item.clientLocalId).trim() : '';
    if (localId && localId.startsWith('LIB-')) out.push(localId);
    const id = item?.id != null ? String(item.id).trim() : '';
    // 若仍是 LIB-* 且已有 url（罕见），也清掉
    if (id && id.startsWith('LIB-')) out.push(id);
  }
  return [...new Set(out)];
}

/**
 * T-A19：扫 LIB-* —— 已有 serverUrl（晚到备份）或超过 TTL 的孤儿键删除。
 * keepIds：当前列表仍标记 ingestPending 的 id，勿删。
 * @returns {Promise<{ scanned: number, deleted: number, ids: string[] }>}
 */
export async function runImageSpaceIdbPeriodicGc(options = {}) {
  const maxAgeMs = Number(options.maxAgeMs) > 0
    ? Number(options.maxAgeMs)
    : IMAGE_SPACE_IDB_TEMP_TTL_MS;
  const keepIds = new Set(
    (Array.isArray(options.keepIds) ? options.keepIds : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean),
  );
  const now = Date.now();

  let allIds = [];
  try {
    allIds = await getAllImageIds();
  } catch (err) {
    console.warn('[imageSpaceIdb] periodic GC list failed:', err?.message || err);
    return { scanned: 0, deleted: 0, ids: [] };
  }

  const tempIds = allIds.filter((id) => isImageSpaceTempLocalId(id));
  const toDelete = [];

  for (const id of tempIds) {
    if (keepIds.has(String(id))) continue;
    let rec = null;
    try {
      rec = await getImageRecordFromIndexedDB(id);
    } catch {
      continue;
    }
    if (!rec) continue;
    if (rec.serverUrl) {
      toDelete.push(String(id));
      continue;
    }
    const savedAt = Number(rec.savedAt) || 0;
    // 无 savedAt 的旧记录：视为可 GC（避免永久占配额）；有 savedAt 则按 TTL
    if (!savedAt || (now - savedAt) > maxAgeMs) {
      toDelete.push(String(id));
    }
  }

  if (toDelete.length > 0) {
    scheduleImageSpaceIdbGc(toDelete);
  }
  return { scanned: tempIds.length, deleted: toDelete.length, ids: toDelete };
}

/**
 * T-A19：配额压力下删除约一半最旧的 LIB-* 临时键（await，便于重试写）。
 * @returns {Promise<{ deleted: number, ids: string[] }>}
 */
export async function reclaimImageSpaceIdbQuota() {
  let allIds = [];
  try {
    allIds = await getAllImageIds();
  } catch {
    return { deleted: 0, ids: [] };
  }
  const tempIds = allIds.filter((id) => isImageSpaceTempLocalId(id));
  if (tempIds.length === 0) return { deleted: 0, ids: [] };

  const records = [];
  for (const id of tempIds) {
    try {
      const rec = await getImageRecordFromIndexedDB(id);
      if (rec) records.push(rec);
    } catch {
      /* skip */
    }
  }
  records.sort((a, b) => (Number(a.savedAt) || 0) - (Number(b.savedAt) || 0));
  const deleteCount = Math.max(1, Math.ceil(records.length / 2));
  const ids = records.slice(0, deleteCount).map((r) => String(r.id));
  try {
    await deleteImagesFromIndexedDB(ids);
  } catch (err) {
    console.warn('[imageSpaceIdb] quota reclaim failed:', err?.message || err);
    return { deleted: 0, ids: [] };
  }
  return { deleted: ids.length, ids };
}

/**
 * T-A19：刷新后从 IDB 恢复未同步的 LIB-* pending 卡（有 blob、无 serverUrl）。
 * @returns {Promise<object[]>}
 */
export async function restoreImageSpacePendingFromIdb(options = {}) {
  const existing = Array.isArray(options.existingImages) ? options.existingImages : [];
  const existingIds = new Set();
  for (const item of existing) {
    if (!item) continue;
    if (item.id != null && item.id !== '') existingIds.add(String(item.id));
    if (item.clientLocalId != null && item.clientLocalId !== '') {
      existingIds.add(String(item.clientLocalId));
    }
  }

  let allIds = [];
  try {
    allIds = await getAllImageIds();
  } catch (err) {
    console.warn('[imageSpaceIdb] restore list failed:', err?.message || err);
    return [];
  }

  const pending = [];
  for (const rawId of allIds) {
    const id = String(rawId || '');
    if (!isImageSpaceTempLocalId(id) || existingIds.has(id)) continue;

    let rec = null;
    try {
      rec = await getImageRecordFromIndexedDB(id);
    } catch {
      continue;
    }
    if (!rec || rec.serverUrl) continue;
    if (!rec.blob && !rec.dataURL) continue;

    let previewUrl = '';
    try {
      if (rec.blob) {
        previewUrl = URL.createObjectURL(rec.blob);
      } else if (typeof rec.dataURL === 'string' && rec.dataURL) {
        previewUrl = rec.dataURL;
      }
    } catch {
      continue;
    }
    if (!previewUrl) continue;

    const savedAt = Number(rec.savedAt) || 0;
    const createdAt = savedAt > 0
      ? format(new Date(savedAt), 'yyyy-MM-dd HH:mm')
      : format(new Date(), 'yyyy-MM-dd HH:mm');

    pending.push({
      id,
      clientLocalId: id,
      name: rec.fileName || id,
      url: '',
      dataUrl: previewUrl,
      src: previewUrl,
      ingestPending: true,
      createdAt,
      size: rec.blob?.size ?? null,
      source: options.uploadOriginSource || 'upload',
      folderId: options.uploadFolderId || '',
      _restoredFromIdb: true,
      ...(options.creatorPayload && typeof options.creatorPayload === 'object'
        ? options.creatorPayload
        : {}),
    });
  }
  return pending;
}

/**
 * T-A19：挂载时启动定时 GC；返回 dispose。
 * @param {{ intervalMs?: number, getKeepIds?: () => string[] }} [options]
 */
export function startImageSpaceIdbMaintenance(options = {}) {
  const intervalMs = Number(options.intervalMs) > 0
    ? Number(options.intervalMs)
    : IMAGE_SPACE_IDB_GC_INTERVAL_MS;
  const getKeepIds = typeof options.getKeepIds === 'function'
    ? options.getKeepIds
    : () => [];

  const run = () => {
    void runImageSpaceIdbPeriodicGc({ keepIds: getKeepIds() }).catch(() => {});
  };
  run();
  const handle = typeof setInterval === 'function'
    ? setInterval(run, intervalMs)
    : null;
  return () => {
    if (handle != null) clearInterval(handle);
  };
}
